import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Stamp, Copy, Pencil, Ban, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/format";

const EMPTY = { name: "", designation: "", email: "", department: "", contact_number: "" };

export default function Approvers() {
  const [approvers, setApprovers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [created, setCreated] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = () => api.get("/approvers").then((r) => setApprovers(r.data.approvers)).catch(() => toast.error("Failed to load approvers"));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/approvers", form);
      setCreated(r.data);
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/approvers/${editTarget.id}`, editForm);
      toast.success("Approver updated");
      setEditTarget(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/approvers/${a.id}`, { status: a.status === "active" ? "inactive" : "active" });
      toast.success(a.status === "active" ? "Approver deactivated" : "Approver reactivated");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="approvers-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Approvers</h1>
          <p className="text-sm text-muted-foreground">Manage approval authorities from your organization.</p>
        </div>
        <Button data-testid="add-approver-btn" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="mr-1.5 h-4 w-4" /> Add Approver
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full text-sm" data-testid="approvers-table">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Designation</th>
              <th className="px-4 py-3 font-medium">Department</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Active</th>
              <th className="px-4 py-3 font-medium text-right">Decisions</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {approvers.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground" data-testid="approvers-empty">
                No approvers yet. Add approvers to build approval chains for your events.
              </td></tr>
            )}
            {approvers.map((a) => (
              <tr key={a.id} data-testid={`approver-row-${a.email}`} className="border-b last:border-0">
                <td className="px-4 py-3"><p className="flex items-center gap-2 font-medium"><Stamp className="h-4 w-4 text-slate-400" />{a.name}</p></td>
                <td className="px-4 py-3 text-sm">{a.designation}</td>
                <td className="px-4 py-3 text-sm">{a.department}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{a.email}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${a.status === "active" ? "border-[#BBF7D0] bg-[#DCFCE7] text-[#166534]" : "border-slate-200 bg-slate-100 text-slate-500"}`} data-testid={`approver-status-${a.email}`}>
                    {a.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-mono">{a.active_steps}</td>
                <td className="px-4 py-3 text-right font-mono">{a.completed_steps}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1.5">
                    <Button variant="outline" size="sm" data-testid={`edit-approver-${a.email}`}
                      onClick={() => { setEditTarget(a); setEditForm({ designation: a.designation, department: a.department, contact_number: a.contact_number }); }}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" data-testid={`toggle-approver-${a.email}`} onClick={() => toggleActive(a)}
                      className={a.status === "active" ? "border-red-200 text-red-700 hover:bg-red-50" : "border-[#BBF7D0] text-[#166534] hover:bg-green-50"}>
                      {a.status === "active" ? <Ban className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="add-approver-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Approver</DialogTitle>
            <DialogDescription>The approver receives an email invitation (mock) and can approve/reject sourcing approvals — full commercial visibility.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Name *</Label><Input data-testid="approver-form-name" required className="mt-1.5" placeholder="Rahul Mehta" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Designation *</Label><Input data-testid="approver-form-designation" required className="mt-1.5" placeholder="Senior Procurement Manager" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></div>
              <div><Label>Email *</Label><Input data-testid="approver-form-email" required type="email" className="mt-1.5" placeholder="rahul@acme.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Department *</Label><Input data-testid="approver-form-department" required className="mt-1.5" placeholder="Procurement" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
              <div className="col-span-2"><Label>Contact Number</Label><Input data-testid="approver-form-contact" className="mt-1.5" placeholder="+91 98XXX XXXXX" value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="approver-form-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add Approver
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!created} onOpenChange={() => setCreated(null)}>
        <DialogContent className="bg-white" data-testid="approver-created-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Approver Added</DialogTitle>
            <DialogDescription>Invitation email logged (mock service — no real email sent). Share these credentials:</DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-slate-50 p-4 font-mono text-sm" data-testid="approver-credentials">
            <p>Email: {created?.email}</p>
            <p className="mt-1">Temporary password: <span className="font-semibold">{created?.temp_password}</span></p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(`${created?.email} / ${created?.temp_password}`); toast.success("Copied"); }}>
              <Copy className="mr-1.5 h-4 w-4" /> Copy
            </Button>
            <Button data-testid="approver-created-close" onClick={() => setCreated(null)} className="bg-slate-900 hover:bg-slate-800">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent className="bg-white" data-testid="edit-approver-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Approver</DialogTitle>
            <DialogDescription>{editTarget?.name} · {editTarget?.email}</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div><Label>Designation</Label><Input data-testid="edit-approver-designation" className="mt-1.5" value={editForm.designation || ""} onChange={(e) => setEditForm({ ...editForm, designation: e.target.value })} /></div>
            <div><Label>Department</Label><Input data-testid="edit-approver-department" className="mt-1.5" value={editForm.department || ""} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} /></div>
            <div><Label>Contact Number</Label><Input data-testid="edit-approver-contact" className="mt-1.5" value={editForm.contact_number || ""} onChange={(e) => setEditForm({ ...editForm, contact_number: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
              <Button type="submit" data-testid="edit-approver-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
